import { Box, Stack, Typography } from "@mui/material";
import { VideoPlayer } from "../../../shared/components/VideoPlayer";
import { useHowToPrepare } from "../hooks";
import { CustomeDialog, Loader } from "../../../shared";
import parse from "html-react-parser";
import emptyIcon from "../../../assets/images/Icons/search-table.png";
import PreparationStepsDialog from "./PreparationStepsDialog";
import { useLocation } from "react-router-dom";
import React from "react";

export const HowToPrepareDetails = ({ recipeId }) => {
  const { search } = useLocation();
  const isHowtoPrepare = search.includes("prepare");
  const { howToPrepareQuery, handleVideoClick, howToPrepare, onImageClick } =
    useHowToPrepare({ recipeId, isHowtoPrepare });

  if (howToPrepareQuery.isLoading) {
    return <Loader />;
  }

  const preparationSteps = (steps) =>
    steps.map((item, index) => {
      return (
        <div key={index}>
          <Stack
            sx={{
              flexDirection: "row",
              gap: "8px",
              alignItems: "center",
              padding: "8px 0px",
            }}
          >
            {item.image_url ? (
              <Box
                component="img"
                src={item.image_url}
                sx={{
                  height: "48px",
                  width: "48px",
                  backgroundColor: "transparent",
                  cursor: "pointer",
                  borderRadius: "8px",
                  backgroundSize: "contain",
                  backgroundPosition: "center",
                  objectFit: "cover",
                  transition:
                    "transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out",
                  "&:hover": {
                    transform: "scale(1.05)",
                    boxShadow: "0 4px 8px rgba(0, 0, 0, 0.15)",
                  },
                }}
                onClick={() => handleVideoClick(true, item)}
              />
            ) : (
              <Box
                sx={{
                  height: "48px",
                  minWidth: "48px",
                  backgroundColor: "#DFE8F2",
                  cursor: "pointer",
                  borderRadius: "8px",
                  backgroundSize: "cover",
                  backgroundPosition: "center",
                  display: "flex", // Enable Flexbox
                  alignItems: "center", // Vertical alignment
                  justifyContent: "center", // Horizontal alignment
                  transition:
                    "transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out",
                  "&:hover": {
                    transform: "scale(1.05)",
                    boxShadow: "0 4px 8px rgba(0, 0, 0, 0.15)",
                  },
                }}
                onClick={() => handleVideoClick(true, item)}
              >
                <img src="/icons/ic_image.svg" alt="placeholder" />
              </Box>
            )}

            <Stack gap="4px">
              <Typography fontSize="12px" fontWeight={600} color="#657083">
                STEP {item.step_number}
              </Typography>
              <Typography
                fontSize="14px"
                fontWeight={400}
                sx={{
                  "& ul, & ol": {
                    marginInlineStart: "15px",
                  },
                }}
              >
                {parse(item.step)}
              </Typography>
            </Stack>
          </Stack>
        </div>
      );
    });

  const EmptyComponent = ({ message }) => {
    return (
      <Stack alignItems="center" gap="8px" backgroundColor="#F7FAFC" py="32px">
        <img src={emptyIcon} alt="empty" height={56} width={56} />
        <Typography
          sx={{ fontWeight: 400, fontSize: "14px", color: "#485364" }}
        >
          {message ?? "No matches found"}
        </Typography>
      </Stack>
    );
  };

  return (
    <Stack>
      <Stack
        direction="row"
        alignItems="center"
        height="54px"
        borderBottom="1px solid"
        borderColor="divider"
      >
        <Typography
          variant="h6"
          fontWeight={600}
          lineHeight="19px"
          minWidth="160px"
        >
          Method
        </Typography>
      </Stack>
      {howToPrepareQuery?.data?.method &&
      howToPrepareQuery?.data?.method !== "<p><br></p>" ? (
        <Stack
          sx={{
            backgroundColor: "#F7FAFC",
            gap: "16px",
            p: "20px",
            "& ul, & ol": {
              paddingInline: "15px",
            },
          }}
        >
          {parse(
            howToPrepareQuery?.data?.method
              ? howToPrepareQuery?.data?.method
              : "",
          )}
        </Stack>
      ) : (
        <EmptyComponent message="No method added yet!" />
      )}

      <Stack>
        <Stack
          direction="row"
          alignItems="center"
          height="54px"
          borderBottom="1px solid"
          borderColor="divider"
        >
          <Typography
            variant="h6"
            fontWeight={600}
            lineHeight="19px"
            minWidth="160px"
          >
            Recipe Preparation Video
          </Typography>
        </Stack>
        {howToPrepareQuery?.data?.videos?.length ? (
          <Box display="grid" gridTemplateColumns="repeat(12, 1fr)" gap={2}>
            {howToPrepareQuery?.data?.videos?.map((video, index) => (
              <Box key={index} gridColumn={{ xs: "span 12", md: "span 6" }}>
                <VideoPlayer video_url={video?.video_url} height="auto" />
              </Box>
            ))}
          </Box>
        ) : (
          <EmptyComponent message="No video added yet!" />
        )}

        <Stack>
          <Stack
            direction="row"
            alignItems="center"
            height="54px"
            borderBottom="1px solid"
            borderColor="divider"
          >
            <Typography
              variant="h6"
              fontWeight={600}
              lineHeight="19px"
              minWidth="160px"
            >
              Preparation Steps
            </Typography>
          </Stack>
          <Stack>
            {howToPrepareQuery?.data?.steps?.length ? (
              preparationSteps(howToPrepareQuery?.data?.steps)
            ) : (
              <EmptyComponent message="No steps added yet!" />
            )}
          </Stack>
        </Stack>
      </Stack>

      <CustomeDialog
        PaperProps={{
          sx: {
            minWidth: { lg: "55vw", md: "80vw", xs: "90%" },
            maxWidth: { lg: "55vw", md: "80vw", xs: "90%" },
          },
        }}
        closeIcon
        handleClose={() => handleVideoClick(false, [])}
        open={howToPrepare?.open}
        divider={false}
        title="Preparation Steps"
        content={
          <PreparationStepsDialog
            state={howToPrepare.items}
            data={howToPrepareQuery?.data?.steps}
            onImageClick={onImageClick}
          />
        }
      />
    </Stack>
  );
};
